// ABOUTME: Generic renderer for a Kratos flow — turns flow.ui.nodes into C* form controls.
// ABOUTME: OIDC buttons + fields + method buttons + messages; no @ory/elements, no DOM hacks.

import { type ReactNode, type SyntheticEvent, useState } from "react"
import type { UiNode, UiText } from "@ory/client"
import { useTranslation } from "react-i18next"
import { CBadge, CButton } from "@shared/ui"
import { cn } from "@shared/lib/utils"
import { AuthField } from "./AuthField"
import { GoogleIcon } from "./icons/GoogleIcon"
import { AppleIcon } from "./icons/AppleIcon"
import { AuthDivider } from "./AuthDivider"
import { signInWithNativeProvider, useOidcMode } from "@/model/auth/nativeOidc"
import {
  autoCompleteHint,
  buildFlowBody,
  htmlInputType,
  inputAttrs,
  isMethodButton,
  isOidcButton,
  isVisibleField,
  kratosMessage,
  messageClass,
  methodOrder,
  nodeLabel,
  oidcProvider,
  seedValues,
} from "./nodeMapping"

interface FlowFormProps {
  flow: { ui: { nodes: UiNode[]; messages?: UiText[] } }
  submitting: boolean
  onSubmit: (body: Record<string, unknown>) => void
  /** Flow context for labelling — "settings" makes the password submit read "Change password". */
  variant?: "login" | "settings"
  /** Extra content (links) rendered under the form. */
  children?: ReactNode
}

const ProviderIcon = ({ provider }: { provider: string }) => {
  if (provider === "google") return <GoogleIcon size={18} />
  if (provider === "apple") return <AppleIcon size={18} />
  return null
}

export const FlowForm = ({ flow, submitting, onSubmit, variant = "login", children }: FlowFormProps) => {
  const { t } = useTranslation()
  const nodes = flow.ui.nodes
  const [values, setValues] = useState<Record<string, string>>(() => seedValues(nodes))

  // Re-seed whenever the flow changes — Kratos rotates csrf_token and echoes fields on each step. Done in the
  // render that sees the new flow, so no step is ever submitted with the previous step's values.
  const [seededFrom, setSeededFrom] = useState({ flow, nodes })
  if (seededFrom.flow !== flow || seededFrom.nodes !== nodes) {
    setSeededFrom({ flow, nodes })
    setValues(seedValues(nodes))
  }

  const submit = (submitNode: UiNode) => onSubmit(buildFlowBody(nodes, submitNode, values))

  // Google rejects OAuth in embedded webviews (disallowed_useragent), so provider
  // buttons render per browser context: "web" submits the flow as usual, "native"
  // hands off to the wrapper app, "hidden" drops the buttons (email/password stays).
  // The native hand-off starts a fresh LOGIN flow, which can't LINK a provider to
  // the current account — the settings variant hides providers in the wrapper too.
  const oidcMode = useOidcMode()
  // Auth screens run at the 44px standalone height; the account page is a form (36px).
  const compact = variant === "settings"
  const showOidc = oidcMode === "web" || (oidcMode === "native" && variant !== "settings")
  const oidc = showOidc ? nodes.filter(isOidcButton) : []
  const fields = nodes.filter(isVisibleField)
  const methodButtons = nodes.filter(isMethodButton).sort((a, b) => methodOrder(a) - methodOrder(b))
  const codeSent = nodes.some((n) => inputAttrs(n)?.name === "code")

  const onFormSubmit = (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault()
    if (methodButtons[0]) submit(methodButtons[0])
  }

  return (
    <form
      onSubmit={onFormSubmit}
      className="flex flex-col gap-4"
    >
      {(flow.ui.messages ?? []).map((m) => (
        <p
          key={m.id}
          className={`text-sm break-words ${messageClass(m.type)}`}
        >
          {kratosMessage(m, t)}
        </p>
      ))}

      {oidc.length > 0 && !compact && (
        <div className="flex flex-col gap-2">
          {oidc.map((node) => {
            const a = inputAttrs(node)!
            const provider = oidcProvider(node) ?? ""
            return (
              <button
                key={`${a.name}-${String(a.value)}`}
                type="button"
                disabled={submitting}
                onClick={() => (oidcMode === "native" ? void signInWithNativeProvider(provider) : submit(node))}
                className={cn(
                  "flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border px-4 text-sm font-medium transition disabled:opacity-60",
                  provider === "apple"
                    ? "border-black bg-black text-white hover:bg-gray-800"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
                )}
              >
                <ProviderIcon provider={provider} />
                {nodeLabel(node, t)}
              </button>
            )
          })}
        </div>
      )}

      {/* In settings a provider is a row: who it is, whether it is linked, and one action. */}
      {oidc.length > 0 && compact && (
        <div className="flex flex-col">
          {oidc.map((node) => {
            const a = inputAttrs(node)!
            const provider = oidcProvider(node) ?? ""
            const linked = a.name === "unlink"
            return (
              <div
                key={`${a.name}-${String(a.value)}`}
                className="flex items-center justify-between gap-3 border-b border-border py-2.5 last:border-b-0"
              >
                <span className="flex min-w-0 items-center gap-2.5 text-sm font-medium">
                  <ProviderIcon provider={provider} />
                  <span className="break-words capitalize">{provider || nodeLabel(node, t)}</span>
                </span>
                <span className="flex flex-none items-center gap-2.5">
                  {linked && <CBadge tone="success">{t("common.connected")}</CBadge>}
                  <CButton
                    type="button"
                    color="gray"
                    className="min-h-[30px] px-3 max-md:min-h-11"
                    isProcessing={submitting}
                    onClick={() => (oidcMode === "native" ? void signInWithNativeProvider(provider) : submit(node))}
                    label={nodeLabel(node, t)}
                  />
                </span>
              </div>
            )
          })}
        </div>
      )}

      {oidc.length > 0 && fields.length > 0 && !compact && <AuthDivider label={t("auth.or")} />}

      {/* Fields and the buttons that submit them are one block: the design keeps 12px
          between a field and the action that follows it. */}
      <div className="flex flex-col gap-3">
        {fields.map((node) => {
          const a = inputAttrs(node)!
          return (
            <AuthField
              key={a.name}
              compact={compact}
              type={htmlInputType(a)}
              label={nodeLabel(node, t)}
              required={a.required}
              autoComplete={autoCompleteHint(a)}
              invalid={node.messages.some((m) => m.type === "error")}
              value={values[a.name] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [a.name]: e.target.value }))}
              messages={node.messages.map((m) => (
                <p
                  key={m.id}
                  className={`text-xs break-words ${messageClass(m.type)}`}
                >
                  {kratosMessage(m, t)}
                </p>
              ))}
            />
          )
        })}

        {methodButtons.map((node) => {
          const a = inputAttrs(node)!
          const isSecondary = methodOrder(node) >= 1 // code / resend rendered as secondary
          return (
            <CButton
              key={`${a.name}-${String(a.value)}`}
              type="button"
              color={isSecondary ? "gray" : "blue"}
              size={compact ? "md" : "touch"}
              className={cn(
                compact ? "self-start px-5 font-semibold max-md:w-full max-md:min-h-11" : "w-full",
                isSecondary ? "font-medium" : "font-semibold hover:bg-primary-700",
              )}
              isProcessing={submitting}
              onClick={() => submit(node)}
              label={nodeLabel(node, t, { codeSent, variant })}
            />
          )
        })}
      </div>

      {children}
    </form>
  )
}
